// Source-of-truth list of every gateable Teamlio panel/page.
//
// `key` is a stable, app-independent identifier used as the permission key in
// an Access profile's `permissions` JSON. Messenger ships an identical catalog
// (same keys) so a single Access controls both apps. `teamlioPath` is the route
// this panel lives at in *this* app — used to gate the sidebar and routes.

export type PanelLevel = "view" | "manage";

export interface PanelDef {
  key: string;
  label: string;
  group: string;
  teamlioPath: string;
  // A "feature" is a sub-permission inside a panel (e.g. an employee-form tab),
  // not a routed page. It has no teamlioPath, never gates the sidebar/routes,
  // and defaults to granted (visible:true) unless explicitly turned off.
  feature?: boolean;
  // For features: the panel key they nest under. They render indented beneath
  // that panel's row and only when the parent is enabled at "manage" level.
  parent?: string;
  // A "leveled" feature carries view/manage semantics (not just on/off). The
  // Access grid renders a View/Manage toggle for it instead of Allowed/Hidden,
  // and `canManage(key)` is meaningful. Used by the Leaves sub-tabs.
  leveled?: boolean;
  // Default cell used by `blankPermissions` when a profile doesn't yet define
  // this key (e.g. a brand-new profile, or an old one predating the feature).
  // Features otherwise default to visible. Set false to make a feature opt-in.
  defaultVisible?: boolean;
  defaultLevel?: PanelLevel;
  // Force View on for EVERY profile/role, always — the panel can never be hidden
  // (View is locked true for all roles). Admins can still raise it to Manage for
  // edit rights. Used for read-for-all pages like Company Policy.
  forceView?: boolean;
}

export const PANEL_CATALOG: PanelDef[] = [
  { key: "dashboard", label: "Dashboard", group: "Main", teamlioPath: "/dashboard" },
  { key: "employees", label: "Employees", group: "Main", teamlioPath: "/employees" },
  { key: "attendance", label: "Attendance", group: "Main", teamlioPath: "/attendance" },
  { key: "leaves", label: "Leaves", group: "Main", teamlioPath: "/leaves" },
  // Self-service inbox — default ON for every profile/role so each user can see
  // their OWN generated paperwork. Still toggleable per profile from the Access
  // panel (not forceView), so admins can hide it for a specific role if needed.
  { key: "documents", label: "Documents", group: "Main", teamlioPath: "/documents", defaultVisible: true },
  { key: "downloads", label: "Downloads", group: "Main", teamlioPath: "/downloads" },
  { key: "separation", label: "Separation Management", group: "Main", teamlioPath: "/separation" },
  // Assets: default ON for every profile/role (self-service "My Assets" — every
  // employee can see what's issued to them). Manage level unlocks the org-wide
  // register (add/edit/assign/approve requests). Profiles saved before this
  // panel existed fall back to defaultVisible, so nobody gets locked out.
  { key: "assets", label: "Assets", group: "Main", teamlioPath: "/assets", defaultVisible: true, defaultLevel: "view" },

  { key: "todo", label: "Daily Planner", group: "Task Management", teamlioPath: "/todo" },
  { key: "projects", label: "Projects", group: "Task Management", teamlioPath: "/projects" },
  { key: "task-reports", label: "Reports", group: "Task Management", teamlioPath: "/tasks/reports" },
  { key: "tasks", label: "Tasks", group: "Task Management", teamlioPath: "/tasks" },
  { key: "clients", label: "Clients", group: "Task Management", teamlioPath: "/clients" },
  { key: "vendors", label: "Vendors", group: "Task Management", teamlioPath: "/vendors" },

  { key: "work-schedule", label: "Work Schedule", group: "Work Monitor", teamlioPath: "/screen-recording" },
  { key: "activity-reports", label: "Activity Reports", group: "Work Monitor", teamlioPath: "/activity-reports" },
  { key: "activity-comparison", label: "Activity Comparison", group: "Work Monitor", teamlioPath: "/activity-comparison" },

  { key: "payroll-records", label: "Payroll Records", group: "Payroll", teamlioPath: "/payroll-record" },
  { key: "payroll-approval", label: "Payroll Approval", group: "Payroll", teamlioPath: "/approval-settings" },
  { key: "payroll-reports", label: "Reports", group: "Payroll", teamlioPath: "/payroll/reports", defaultVisible: true },

  { key: "set-company", label: "Company", group: "Settings", teamlioPath: "/settings/company" },
  { key: "set-office", label: "Office", group: "Settings", teamlioPath: "/settings/office" },
  { key: "set-department", label: "Department", group: "Settings", teamlioPath: "/settings/department" },
  { key: "set-designation", label: "Designation", group: "Settings", teamlioPath: "/settings/designation" },
  { key: "set-terms", label: "Terms & Conditions", group: "Settings", teamlioPath: "/settings/terms-and-conditions" },
  // Company Policy: View is forced on for every role, always (can't be hidden);
  // admins can still raise a profile to Manage for add/update/delete rights.
  { key: "set-company-policy", label: "Company Policy", group: "Settings", teamlioPath: "/settings/company-policy", forceView: true },
  { key: "set-startupkhata", label: "Startup Khata", group: "Settings", teamlioPath: "/settings/startupkhata" },
  { key: "set-task-settings", label: "Task Settings", group: "Settings", teamlioPath: "/settings/task-code" },
  { key: "set-access-management", label: "Access Management", group: "Settings", teamlioPath: "/settings/access-management" },
  { key: "set-roles", label: "Roles", group: "Settings", teamlioPath: "/settings/roles" },
  { key: "set-announcements", label: "Announcements", group: "Settings", teamlioPath: "/settings/announcements" },

  // Employee-form sub-sections (gated inside the Add/Edit Employee form when the
  // user has the Employees panel at Manage). Personal Details & Review are always
  // available — they're required to create/save an employee.
  { key: "emp-salary", label: "Salary & Deductions", group: "Main", teamlioPath: "", feature: true, parent: "employees" },
  { key: "emp-documents", label: "Upload Documents", group: "Main", teamlioPath: "", feature: true, parent: "employees" },

  // Leaves sub-tabs — each tab inside /leaves is gated as a leveled feature.
  // View = can see the tab (read-only); Manage = can act (apply, approve, edit).
  // When a profile doesn't specify one, the Leaves page falls back to role logic.
  { key: "leaves-my", label: "My Leaves (apply / cancel own)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: true, defaultLevel: "manage" },
  { key: "leaves-approvals", label: "Pending Approvals (approve / reject)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: false, defaultLevel: "manage" },
  { key: "leaves-team", label: "All Team Requests", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: false, defaultLevel: "view" },
  { key: "leaves-holidays", label: "Holidays (add / edit / remove)", group: "Main", teamlioPath: "", feature: true, parent: "leaves", leveled: true, defaultVisible: true, defaultLevel: "view" },

  // Dashboard cards — individual tiles, gated per Access profile (Visible +
  // View/Manage). Each maps to a `data-chart` tile in Dashboard.tsx. When a
  // profile doesn't specify a card the role default applies (employees see the
  // four personal cards; admins/managers see the org cards).
  { key: "dash-quick-actions", label: "Quick Actions & Activities", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-weekly-productivity", label: "Weekly Productivity / Daily Presence", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-monthly-presence", label: "Monthly Presence Calendar", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-weekly-task-flow", label: "Weekly Task Flow", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-project-execution", label: "Project Execution Progress", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-monthly-productivity", label: "Monthly Productivity", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-tasks-priority", label: "Tasks by Priority", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-tasks-status", label: "Tasks Health (Status Mix)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-leaves-pipeline", label: "Leaves Pipeline", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-workforce", label: "Workforce", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-projects", label: "Projects (Dashboard)", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-attendance-health", label: "Attendance Health", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
  { key: "dash-org-pulse", label: "Organisation Pulse", group: "Main", teamlioPath: "", feature: true, parent: "dashboard" },
];

export const PANEL_GROUPS: string[] = PANEL_CATALOG.reduce<string[]>((acc, p) => {
  if (!acc.includes(p.group)) acc.push(p.group);
  return acc;
}, []);

// Match a pathname to a panel key. Longest teamlioPath wins so "/tasks/reports"
// resolves to task-reports rather than the broader "tasks".
export function panelKeyForPath(pathname: string): string | null {
  const sorted = [...PANEL_CATALOG].sort(
    (a, b) => b.teamlioPath.length - a.teamlioPath.length
  );
  for (const p of sorted) {
    if (!p.teamlioPath) continue; // feature sub-permission — not a routed page
    if (pathname === p.teamlioPath || pathname.startsWith(p.teamlioPath + "/")) {
      return p.key;
    }
  }
  return null;
}

export function panelByKey(key: string): PanelDef | undefined {
  return PANEL_CATALOG.find((p) => p.key === key);
}
